import React, { useState, useEffect } from "react";
import { CheckSquare, Search } from "lucide-react";
import { DataTable, Button } from "@/components/ui";
import { fmt } from "@/lib/formatters";
import api from "@/lib/API";
import toast from "react-hot-toast";
import { FilterSection } from "@/components/ui/FilterSection";
import { fetchSales } from "../../inventory/services/inventoryService";

export default function SaleApprovalTab() {
  const [sales, setSales] = useState([]);
  const [search, setSearch] = useState("");
  const [processingId, setProcessingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ date: "all", fromDate: "", toDate: "", status: "Pending" });

  const STATUS_OPTIONS = [
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" }
  ];

  const handleSetFilters = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const loadSales = async () => {
    try {
      setLoading(true);
      const res = await fetchSales();
      setSales(Array.isArray(res.data?.data || res.data) ? (res.data?.data || res.data) : []);
    } catch (err) {
      console.error("Failed to load sales", err);
      toast.error("Failed to load sales approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSales();
  }, []);

  const filteredSales = React.useMemo(() => {
    let result = [...sales];

    if (search) {
      const term = search.toLowerCase();
      result = result.filter(s =>
        (s.invoiceNo || '').toLowerCase().includes(term) ||
        (s.customer || '').toLowerCase().includes(term) ||
        (s.projectCustomId || '').toLowerCase().includes(term)
      );
    }

    const { date, fromDate, toDate, status } = filters;

    if (status && status !== "all") {
      result = result.filter(s => String(s.approvalStatus || "").toLowerCase() === String(status).toLowerCase());
    }

    if (date !== "all" || fromDate || toDate) {
      let startDate = null;
      let endDate = null;

      const now = new Date();
      if (date && date !== "all") {
        startDate = new Date();
        switch (date) {
          case "today": startDate.setHours(0, 0, 0, 0); break;
          case "this_week": case "week": startDate.setDate(now.getDate() - 7); break;
          case "this_month": case "month": startDate.setDate(now.getDate() - 30); break;
          case "this_year": case "year": startDate.setDate(now.getDate() - 365); break;
        }
        endDate = now;
      } else {
        if (fromDate) startDate = new Date(fromDate);
        if (toDate) endDate = new Date(toDate);
      }

      if (startDate || endDate) {
        result = result.filter((s) => {
          const createdAt = new Date(s.date || s.createdAt);
          if (startDate && createdAt < startDate) return false;
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            if (createdAt > end) return false;
          }
          return true;
        });
      }
    }

    return result;
  }, [sales, search, filters]);

  const handleAction = async (id, action) => {
    try {
      setProcessingId(id);
      await api.put(`/api/inventory/sales/${id}/${action}`);
      toast.success(`Sale successfully ${action}d!`);
      loadSales();
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${action} sale`);
    } finally {
      setProcessingId(null);
    }
  };

  const columns = [
    {
      header: "Date",
      render: (s) => (
        <span className="text-[12px] font-normal text-slate-500 font-['Poppins']">
          {new Date(s.date).toLocaleDateString()}
        </span>
      )
    },
    {
      header: "Invoice No",
      render: (s) => (
        <span className="text-[12px] font-semibold text-[#2b448f] font-['Poppins']">
          {s.invoiceNo || '—'}
        </span>
      )
    },
    {
      header: "Customer",
      render: (s) => (
        <span className="text-[13px] font-normal text-slate-700 uppercase">{s.customer || '—'}</span>
      )
    },
    {
      header: "Amount",
      render: (s) => (
        <span className="text-[13px] font-normal text-slate-800 font-['Poppins']">
          {fmt(Number(s.netPayable || 0))}
        </span>
      )
    },
    {
      header: "Actions",
      className: "text-right",
      render: (s) => (
        <div className="flex items-center justify-end gap-2">
          <Button 
            variant="outline" 
            size="sm" 
            className="text-emerald-600 border-emerald-200 hover:bg-emerald-50 h-8"
            disabled={processingId === s._id}
            onClick={() => handleAction(s._id, 'approve')}
          >
            {processingId === s._id ? "..." : "Approve"}
          </Button>
          <Button 
            variant="outline" 
            size="sm"
            className="text-rose-600 border-rose-200 hover:bg-rose-50 h-8"
            disabled={processingId === s._id}
            onClick={() => handleAction(s._id, 'reject')}
          >
            {processingId === s._id ? "..." : "Reject"}
          </Button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-in fade-in duration-500">

      <div className="flex flex-col rounded-2xl overflow-hidden bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] min-w-0">
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={handleSetFilters}
          showExport={false}
          statusOptions={STATUS_OPTIONS}
          statusPlaceholder="All Statuses"
        />
      </div>

      {loading ? (
        <div className="flex justify-center p-8">Loading...</div>
      ) : filteredSales.length === 0 ? (
        <div className="card p-12 flex flex-col items-center justify-center bg-white shadow-sm border border-slate-100 rounded-2xl">
          <div className="w-16 h-16 bg-emerald-50 text-emerald-500 rounded-full flex items-center justify-center mb-4">
            <CheckSquare size={32} />
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-1">No Results</h2>
          <p className="text-slate-500 text-sm">No sales found matching your current filters.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
          <DataTable columns={columns} data={filteredSales} noScroll={true} />
        </div>
      )}
    </div>
  );
}
